///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { BaseLoader } from "./BaseLoader";
import { UpdaterController, UpdateType } from "./UpdaterController";

export class VsfXStreamingLoader extends BaseLoader {
  override async load(): Promise<void> {
    if (!this.viewer.visualizeJs) return;

    const visLib = this.viewer.visLib();
    const visViewer = visLib.getViewer();
    const abortController = new AbortController();

    const updaterController = new UpdaterController();
    updaterController.initialize(this.viewer);

    this.viewer._abortController = abortController;

    let isFireDatabaseChunk = false;

    const chunkLoadHandler = (progress: number, chunk: Uint8Array) => {
      if (!this.viewer.visualizeJs) return;

      const status = visViewer.parseVsfx(chunk);
      updaterController.update(UpdateType.kDelay);

      this.viewer.emitEvent({ type: "geometryprogress", data: progress, model: this.model });

      let state = false;
      if (
        status === visLib.DatabaseStreamStatus.ReadyServiceData ||
        (status === visLib.DatabaseStreamStatus.Complete && !isFireDatabaseChunk)
      ) {
        isFireDatabaseChunk = true;
        state = true;
      }

      if (state) {
        updaterController.update(UpdateType.kForce);

        this.viewer.syncOpenCloudVisualStyle(false);
        this.viewer.syncOptions();
        this.viewer.syncOverlay();
        this.viewer.resize();

        this.viewer.emitEvent({ type: "databasechunk", data: chunk, model: this.model });
      } else {
        this.viewer.emitEvent({ type: "geometrychunk", data: chunk, model: this.model });
      }
    };

    console.time("File load time");
    try {
      this.viewer.emitEvent({ type: "geometrystart", model: this.model });

      await this.model.downloadResource(this.model.database, chunkLoadHandler, abortController.signal);
      console.timeEnd("File load time");

      updaterController.update(UpdateType.kNormal);

      this.viewer.emitEvent({ type: "geometryend", model: this.model });
    } catch (error: any) {
      this.viewer.emitEvent({ type: "geometryerror", data: error, model: this.model });
      throw error;
    }
  }
}
